import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Search, Loader2, Clock, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RECENT_SEARCHES = 5;

export const GlobalSearch = ({ placeholder }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRecent, setShowRecent] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load recent searches from localStorage
  useEffect(() => {
    if (!user) return;

    const storageKey = `impi_recent_searches_${user.id}`;
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setRecentSearches(Array.isArray(parsed) ? parsed : []);
      } catch (error) {
        console.error('Error parsing recent searches:', error);
        setRecentSearches([]);
      }
    }
  }, [user]);

  // Save recent searches to localStorage
  const saveRecentSearches = (searches) => {
    if (!user) return;

    const storageKey = `impi_recent_searches_${user.id}`;
    localStorage.setItem(storageKey, JSON.stringify(searches));
    setRecentSearches(searches);
  };

  // Add search to recent searches
  const addRecentSearch = (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 2) return;

    const trimmed = searchTerm.trim();
    let updated = [...recentSearches];

    // Remove if already exists
    updated = updated.filter(s => s !== trimmed);

    // Add to beginning
    updated.unshift(trimmed);

    // Keep only last 5
    if (updated.length > MAX_RECENT_SEARCHES) {
      updated = updated.slice(0, MAX_RECENT_SEARCHES);
    }

    saveRecentSearches(updated);
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    saveRecentSearches([]);
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setShowRecent(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setShowDropdown(false);
      setShowRecent(false);
      return;
    }

    // Show recent searches if query is too short
    if (query.trim().length < 2) {
      setResults([]);
      setShowDropdown(false);
      if (recentSearches.length > 0) {
        setShowRecent(true);
      }
      return;
    }

    setShowRecent(false);

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/trademarks`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Filter results client-side
        const filtered = response.data.filter(tm => {
          const searchTerm = query.toLowerCase();
          return (
            tm.expediente.toLowerCase().includes(searchTerm) ||
            (tm.registro && tm.registro.toLowerCase().includes(searchTerm)) ||
            (tm.denominacion && tm.denominacion.toLowerCase().includes(searchTerm))
          );
        }).slice(0, 8);

        setResults(filtered);
        setShowDropdown(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [query, token, recentSearches.length]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    // Handle recent searches
    if (showRecent) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev < recentSearches.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedIndex >= 0 && recentSearches[selectedIndex]) {
            handleSelectRecentSearch(recentSearches[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setShowRecent(false);
          setSelectedIndex(-1);
          break;
        default:
          break;
      }
      return;
    }

    // Handle search results
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleSelectTrademark(results[selectedIndex]);
        } else if (results.length > 0) {
          handleSelectTrademark(results[0]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleSelectTrademark = (trademark) => {
    addRecentSearch(query);
    navigate(`/trademarks/${trademark.id}`);
    setQuery('');
    setShowDropdown(false);
    setShowRecent(false);
    setSelectedIndex(-1);
  };

  const handleSelectRecentSearch = (searchTerm) => {
    setQuery(searchTerm);
    setShowRecent(false);
    setSelectedIndex(-1);
    // Trigger search automatically
  };

  const handleInputFocus = () => {
    if (!query.trim() || query.trim().length < 2) {
      if (recentSearches.length > 0) {
        setShowRecent(true);
      }
    } else {
      if (results.length > 0) {
        setShowDropdown(true);
      }
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'status-neutral';
    if (status.includes('Registrada') || status.includes('Vigente')) return 'status-success';
    if (status.includes('Trámite') || status.includes('Examen')) return 'status-info';
    if (status.includes('Pendiente') || status.includes('Vencer')) return 'status-warning';
    if (status.includes('Vencida') || status.includes('Cancelada')) return 'status-error';
    return 'status-neutral';
  };

  return (
    <div className="relative w-full md:max-w-md" ref={searchRef}>
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder={placeholder || "Buscar marca por expediente, registro o nombre..."}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          className="w-full h-9 pl-10 pr-10 bg-gray-50 border border-gray-200 rounded-md text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          aria-label="Búsqueda global de marcas"
          data-testid="global-search-input"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
        )}
      </div>

      {/* Recent Searches Dropdown */}
      {showRecent && recentSearches.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50"
          data-testid="recent-searches-dropdown"
        >
          <div className="px-4 py-2 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Búsquedas recientes
              </span>
              <button
                onClick={clearRecentSearches}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="clear-recent-searches"
              >
                Limpiar
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {recentSearches.map((search, index) => (
              <button
                key={index}
                onClick={() => handleSelectRecentSearch(search)}
                className={`w-full px-4 py-3 text-left border-b border-gray-100 last:border-b-0 transition-colors ${index === selectedIndex ? 'bg-gray-50' : 'hover:bg-gray-50'
                  }`}
                data-testid={`recent-search-${index}`}
              >
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700">{search}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results Dropdown */}
      {showDropdown && !showRecent && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50"
          role="listbox"
          aria-label="Resultados de búsqueda"
          data-testid="search-results-dropdown"
        >
          {loading ? (
            <div className="p-4 text-center">
              <Loader2 className="h-5 w-5 text-gray-400 animate-spin mx-auto" />
            </div>
          ) : results.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500">
              No se encontraron marcas
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto">
              {results.map((trademark, index) => (
                <button
                  key={trademark.id}
                  onClick={() => handleSelectTrademark(trademark)}
                  className={`w-full px-4 py-3 text-left border-b border-gray-100 last:border-b-0 transition-colors ${index === selectedIndex ? 'bg-gray-50' : 'hover:bg-gray-50'
                    }`}
                  role="option"
                  aria-selected={index === selectedIndex}
                  data-testid={`search-result-${trademark.id}`}
                >
                  <div className="flex flex-col gap-1 w-full">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {trademark.denominacion || <span className="italic text-gray-500">Sin denominación</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                      <span className="font-mono">Exp: {trademark.expediente}</span>
                      {trademark.registro && (
                        <>
                          <span>•</span>
                          <span className="font-mono">Reg: {trademark.registro}</span>
                        </>
                      )}
                      {trademark.fecha_vigencia && (
                        <>
                          <span>•</span>
                          <span>Vigencia: {new Date(trademark.fecha_vigencia).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
